import CompaniesAPI from '../../api/companies';
import * as types from '../mutation-types';

const T = types.default;

let latestFetchId = 0;

export const state = {
  records: [],
  meta: { count: 0, currentPage: 1, perPage: 25 },
  uiFlags: {
    isFetching: false,
    isSaving: false,
    isDeleting: false,
  },
};

export const getters = {
  getCompanies: $state => $state.records,
  getMeta: $state => $state.meta,
  getUIFlags: $state => $state.uiFlags,
};

export const actions = {
  async fetch({ commit }, params = {}) {
    latestFetchId += 1;
    const fetchId = latestFetchId;
    commit(T.SET_COMPANIES_UI_FLAG, { isFetching: true });
    try {
      const { data } = await CompaniesAPI.list(params);
      if (fetchId === latestFetchId) commit(T.SET_COMPANIES, data);
    } finally {
      if (fetchId === latestFetchId) {
        commit(T.SET_COMPANIES_UI_FLAG, { isFetching: false });
      }
    }
  },
  async create({ commit }, company) {
    commit(T.SET_COMPANIES_UI_FLAG, { isSaving: true });
    try {
      const { data } = await CompaniesAPI.create(company);
      commit(T.ADD_COMPANY, data.payload);
      return data.payload;
    } finally {
      commit(T.SET_COMPANIES_UI_FLAG, { isSaving: false });
    }
  },
  async update({ commit }, { id, ...company }) {
    commit(T.SET_COMPANIES_UI_FLAG, { isSaving: true });
    try {
      const { data } = await CompaniesAPI.update(id, company);
      commit(T.EDIT_COMPANY, data.payload);
      return data.payload;
    } finally {
      commit(T.SET_COMPANIES_UI_FLAG, { isSaving: false });
    }
  },
  async delete({ commit }, id) {
    commit(T.SET_COMPANIES_UI_FLAG, { isDeleting: true });
    try {
      const response = await CompaniesAPI.delete(id);
      // 202: the company has too many contacts and is deleted by a background job,
      // so it still exists and must stay in the list until that job runs.
      const queued = response?.status === 202;
      if (!queued) commit(T.REMOVE_COMPANY, id);
      return { queued };
    } finally {
      commit(T.SET_COMPANIES_UI_FLAG, { isDeleting: false });
    }
  },
};

export const mutations = {
  [T.SET_COMPANIES]($state, { payload = [], meta = {} }) {
    $state.records = payload.map(company => Object.freeze(company));
    $state.meta = {
      count: meta.count || 0,
      currentPage: meta.current_page || 1,
      perPage: meta.per_page || $state.meta.perPage,
    };
  },
  [T.ADD_COMPANY]($state, company) {
    $state.records = [Object.freeze(company), ...$state.records];
    $state.meta = { ...$state.meta, count: $state.meta.count + 1 };
  },
  [T.EDIT_COMPANY]($state, company) {
    $state.records = $state.records.map(record =>
      record.id === company.id ? Object.freeze(company) : record
    );
  },
  [T.REMOVE_COMPANY]($state, id) {
    $state.records = $state.records.filter(record => record.id !== id);
    $state.meta = { ...$state.meta, count: Math.max($state.meta.count - 1, 0) };
  },
  [T.SET_COMPANIES_UI_FLAG]($state, flag) {
    $state.uiFlags = { ...$state.uiFlags, ...flag };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
